import { DispatcherEvent } from '@carvana/merch-native';
import { useCallback, useContext } from 'react';
import PrimaryCtaType from '~/constants/PrimaryCtaType';
import { useIsNative } from '~/context/common/DeviceContext';
import ModalContext from '~/context/vdp/ModalContext';
import { useGetVehicleStockNumber } from '~/context/vdp/VehicleContext';
import { useViewSimilarVehicles } from '~/hooks/vdp/cta';
import usePrimaryCtaButtonState from '~/hooks/vdp/selectors/usePrimaryCtaButtonState';
import { useEventPayload } from '~/hooks/vdp/selectors/useVehicleDetailsForNative';
import sendPurchaseMessageToNative from './sendPurchaseMessageToNative';
import useGetStarted from './useGetStarted';
import usePreOrder from './usePreOrder';

const dummyFunction = () => {};

function useClickHandler(): () => void {
  const { setLockTimerModalOpen, setSignUpForAlertsModalOpen } =
    useContext(ModalContext);
  const eventPayload = useEventPayload();
  const stockNumber = useGetVehicleStockNumber();
  const isNative = useIsNative();
  const { isDisabled, type } = usePrimaryCtaButtonState();

  const getStarted = useGetStarted(stockNumber);
  const preOrder = usePreOrder();
  const viewSimilarVehicles = useViewSimilarVehicles();

  const continuePurchase = useCallback(() => {
    if (isNative) {
      sendPurchaseMessageToNative(
        DispatcherEvent.ContinuePurchase,
        eventPayload
      );
    } else {
      window.location.href = `/purchase/dashboard?stock-number=${stockNumber}`;
    }
  }, [eventPayload, isNative, stockNumber]);

  const getAlerts = useCallback(() => {
    setSignUpForAlertsModalOpen(true);
  }, [setSignUpForAlertsModalOpen]);

  const notifyMe = useCallback(() => {
    setLockTimerModalOpen(true);
  }, [setLockTimerModalOpen]);

  if (isDisabled) return dummyFunction;

  switch (type) {
    case PrimaryCtaType.Default:
    case PrimaryCtaType.DisabledGetStarted:
    case PrimaryCtaType.DisabledPreOrder:
      return dummyFunction;
    case PrimaryCtaType.ContinuePurchase:
      return continuePurchase;
    case PrimaryCtaType.GetStarted:
      return getStarted;
    case PrimaryCtaType.NotifyMe:
      return notifyMe;
    case PrimaryCtaType.ViewSimilarVehicles:
      return viewSimilarVehicles;
    case PrimaryCtaType.GetAlerts:
      return getAlerts;
    case PrimaryCtaType.Reservable:
      return preOrder;
    default:
      return dummyFunction;
  }
}

export default function usePrimaryButtonHandler(
  onClick: () => void = dummyFunction
) {
  const handleClick = useClickHandler();

  return useCallback(() => {
    onClick();
    handleClick();
  }, [handleClick, onClick]);
}
